import { BaseRowStore, TableStore } from "../tablestore";
import { NodeStore } from "./node/NodeStore";
import { DataState, _STATUS } from "../tablestore/types";
import { _ } from "ynf-core-middleware";
import { makeObservable, observable } from 'mobx';
import { findNodeByKeyDFS, flattenStatusItems, flatten, listToTree, AdapterType, flattenWithoutChildren } from './utils'
export class TreeTableStore extends TableStore {
  lazyLoadTree: boolean
  parentKey: string
  adapterType: string
  initial: boolean
  constructor(storeParams?: any, rootStore?: any, extendRowStore?: any, primaryKey?: string) {
    super(storeParams, rootStore, extendRowStore, primaryKey)
    this.extendRowStore = extendRowStore || NodeStore;
    // @ts-ignore
    // this.primaryKey = primaryKey;
    this.parentKey = storeParams.parentKey || 'parent'
    this.adapterType = storeParams.adapterType || 'tree'
    this.storeType = 'TreeTableStore';
    this.lazyLoadTree = false;
    this.initial = true;
    makeObservable(this, {
      lazyLoadTree: observable
    })
  }

  getRow (index: number): BaseRowStore<any> {
    const flattenData = flatten(this.data)
    const filterData = this.filterByDeleteStatus(flattenData)
    return filterData?.[index];
  }

  setLazyLoadTreeTag(tag: any) {
    this.lazyLoadTree = tag
  }

  /**
   * 拍平的RowStore data
   *  */
  get flatValue(): Array<any> {
    const flattenData: Array<any> = [];

    const flattenRow = (rowStore: BaseRowStore<any>) => {
      const rowValue = { ...rowStore?.getValue(), rowKey: rowStore.rowKey, rowStore };

      if (rowStore?.data?.children && rowStore?.data?.children?.length > 0) {
        rowValue.children = rowStore?.data?.children?.filter((cr: any) => 
          cr?.get_Status() != DataState.Delete
        ).map((childRowStore: BaseRowStore<any>) => {
          return flattenRow(childRowStore);
        });
      }
      return rowValue
    };

    this.getValue().forEach((rowStore) => {
      flattenData.push(flattenRow(rowStore));
    });

    return flattenData;
  }

  /**
   * 新增节点(默认增加到最后：children最后 or 外层最后)
   * @param nodeData 节点数据
   * @param parent 父节点：parentKey | nodeStore
   */
  addNode(nodeData: any, parent?: string | BaseRowStore<any>) {
    if (parent !== undefined) {
      const node = this.getNode(parent)
      const nodeStore = node?.addChildNode(nodeData)
      this.hooks.afterAddRow?.forEach((fn: Function) => { fn?.(nodeStore) });
      return nodeStore
    } else {
      return this.addRow(nodeData)
    }
  }

  /**
   * 获取节点
   * @param key 建议使用key or nodeStore
   */
  getNode(key: string | number | BaseRowStore<any>) {
    if (key instanceof BaseRowStore) {
      return key
    }else if (typeof key === 'number') {
      return this.getRow(key)
    } else {
      return findNodeByKeyDFS(key, this.data)
    }
  }

  /**
   * 更新节点
   * @param key 
   * @param nodeData 
   */
  updateNode(nodeData: any, key: string): void {
    const node = this.getNode(key)
    if (node?.hasParent) {
      const parentNode = node?.getParent()
      const newNode = this.parseTreeRow(nodeData, parentNode)
      parentNode.getChildren()?.forEach((n: BaseRowStore<any>, index: number) => {
      if (n.rowKey === node.rowKey) {
        parentNode.getChildren()[index] = newNode
      }
      if ([DataState.Unchanged, undefined].includes(newNode.get_Status())) {
      newNode.set_Status(DataState.Update);
    }
    })
    } else {
      this.updateRow(key, nodeData)
    }
  }

  /**
   * 删除节点
   * @param key 
   */
  deleteNode(key: string | number | BaseRowStore<any>) {
    const node = this.getNode(key)
    if (node?.hasParent) {
      const parentNode = node.getParent()
      parentNode.deleteChildNode(node)
    } else {
      this.deleteRow(node)
    }
  }

  /**
   * 插入节点，默认插入当前节点同级下(copy通用场景)
   * @param nodeData 
   * @param key 
   */
  insertNode(nodeData: any, key: string | number | BaseRowStore<any>) {
    const node = this.getNode(key)
    const targetRowKey = node?.rowKey
    if (node?.hasParent) {
      const parentNode = node.getParent()
      const children = parentNode.getChildren()
      const index = children.findIndex(({ rowKey }: any) => rowKey === targetRowKey);
      // 查找当前节点的位置
      return parentNode.insertChildNode(index + 1, nodeData, parentNode)
    } else {
      const index = this.data?.findIndex(({ rowKey }: any) => rowKey === targetRowKey);
      return this.insertRow(index + 1, nodeData)
    }
  }

  parseTreeRow(data: any, parent: BaseRowStore<any>) {
    if (data instanceof BaseRowStore) {
      data = data.dataToJson();
    }
    // 如果data包含children是树结构，node节点上处理
    const node = new this.extendRowStore(this, data, parent);
    // 新增的节点需要记录parent
    node?.setValue(this.parentKey, parent?.getValue('id'))
    node?.setValue(`${this.parentKey}_name`, parent?.getValue('name'))
    return node
  }

  /** 
   * 有数据变动的行 */
  getChangedRows() {
    return flattenStatusItems(this.data, (node: any) => [DataState.Insert, DataState.Update, DataState.Delete].includes(node.get_Status()))
  }

  flattenChildrenData(data: Array<any>) {
    if (data?.length > 0) {
      data.forEach((item: any) => {
        if (item.isEnd == 0) {
          item.children = [{'_loadMoreData': true}]
        }
      })
    }
    return []
  }

  /**
   * 校验
   * @returns 校验结果
   */
  validate() {
    const result: any = [];
    const flattenData = flatten(this.data)
    flattenData.forEach((row: BaseRowStore<any>) => {
      result.push(row.validate());
    });
    return result || [];
  }


  filterData(treeData?: any): any {
    let data = treeData || this.data
    if (this.filters.length) {
      for (let filter of this.filters) {
        if (filter.fn) filter = filter.fn;
        data = data.filter(filter);
      }
    }
    return data
  }

  setValue(data: Array<Object> = []): void {
    if (!this.initial) {
      this.initial = true;
    }
    if (this.adapterType == AdapterType.Tree) {
      this.setTreeValue(data)
    } else if (this.adapterType == AdapterType.List) {
      this.setListValue(data)
    } else {
      throw (new Error(`Please set the adapterType`))
    }
  }

  dataEntityToJson() {
    if (this.adapterType == AdapterType.Tree) {
      return this.dataTreeEntityToJson()
    } else if (this.adapterType == AdapterType.List) {
      return this.dataListEntityToJson()
    } else {
      throw (new Error(`Please set the adapterType`))
    }
  }

  getRows({ withFilter = true } = {}): any {
    if (this.adapterType == AdapterType.Tree) {
      return this.getTreeRows({withFilter})
    } else if (this.adapterType == AdapterType.List) {
      return this.getListRows({withFilter})
    } else {
      throw (new Error(`Please set the adapterType`))
    }
  }

  // getValue({ withFilter = true } = {}): any {
  //   if (this.adapterType == AdapterType.Tree) {
  //     return this.getTreeValue({withFilter})
  //   } else if (this.adapterType == AdapterType.List) {
  //     return this.getListValue({withFilter})
  //   } else {
  //     throw (new Error(`Please set the adapterType`))
  //   }
  // }

  getListRows({ withFilter = true } = {}) {
    const flattenData = flatten(this.data)
    const data = withFilter ? this.filterData(flattenData) : flattenData;
    return data
  }

  getTreeRows({ withFilter = true } = {}) {
    return super.getRows({withFilter})
  }

  // getListValue({ withFilter = true } = {}) {
  //   const flattenData = flattenWithoutChildren(this.data)
  //   const data = withFilter ? this.filterData(flattenData) : flattenData;
  //   return this.showSelect ? this.filterSelectValue(data) : this.filterByDeleteStatus(data);
  // }

  // getTreeValue({ withFilter = true } = {}) {
  //   super.getValue({ withFilter})
  // }

  reBuildTreeData(list: Array<any>) {
    const data = list || this.dataEntityToJson()
    const handleData = flattenWithoutChildren(data)
    this.setValue(handleData)
  }


  setTreeValue(tree: Array<Object> = []) : void {
    super.setValue(tree)
  }

  setListValue(list: Array<Object> = []) : void{
    const treeData = listToTree(list, this.parentKey)
    super.setValue(treeData)
  }

  dataListEntityToJson(): any {
    const flattenData = flatten(this.data)
    const rows = flattenData.map((value:any) => {
      return value.dataEntityToJson();
    })
    return rows
  }

  dataTreeEntityToJson(): any {
    return super.dataEntityToJson()
  }
}

